/**
 * chartsLazy.tsx — Perf 3g: the lazy door to ChartComponents (i.e. recharts).
 *
 * recharts + d3 is ~101KB gzip, and it reached the EAGER bundle through
 * exactly four static paths, all of them widget files (the fourth ran
 * AppShell → SettingsPage → MetricHubSettings → SparklineDetailDialog — which
 * is why lazying "the three named AppShell imports" was measured to do
 * nothing; the graph had to be severed HERE, below every path). The three
 * consumers keep their exact call signatures — only their import path changes
 * to './chartsLazy' — and each chart carries its own Suspense with a quiet
 * placeholder, so a first render shows a blank chart body for a beat instead
 * of every visitor downloading d3 before the login form.
 *
 * NOTE for future imports: adding a NEW static `from './ChartComponents'`
 * anywhere reachable from the eager graph silently undoes this. Import from
 * this file instead, or make the surface itself lazy.
 */
import React, { Suspense } from 'react';
import type { ComponentProps } from 'react';
import type * as CC from './ChartComponents';

const load = () => import('./ChartComponents');

const LazyLine = React.lazy(() => load().then((m) => ({ default: m.InteractiveLineChart })));
const LazyBar = React.lazy(() => load().then((m) => ({ default: m.InteractiveBarChart })));
const LazyPie = React.lazy(() => load().then((m) => ({ default: m.InteractivePieChart })));
const LazyDonut = React.lazy(() => load().then((m) => ({ default: m.InteractiveDonutChart })));
const LazyNumberTrend = React.lazy(() => load().then((m) => ({ default: m.NumberTrendWidget })));

/** Neutral, parent-sized placeholder — charts live in sized containers, so a
 *  quiet fill is less jarring than a spinner for the sub-second load. */
const fallback = <div style={{ width: '100%', minHeight: 80 }} />;

export function InteractiveLineChart(props: ComponentProps<typeof CC.InteractiveLineChart>) {
  return <Suspense fallback={fallback}><LazyLine {...props} /></Suspense>;
}
export function InteractiveBarChart(props: ComponentProps<typeof CC.InteractiveBarChart>) {
  return <Suspense fallback={fallback}><LazyBar {...props} /></Suspense>;
}
export function InteractivePieChart(props: ComponentProps<typeof CC.InteractivePieChart>) {
  return <Suspense fallback={fallback}><LazyPie {...props} /></Suspense>;
}
export function InteractiveDonutChart(props: ComponentProps<typeof CC.InteractiveDonutChart>) {
  return <Suspense fallback={fallback}><LazyDonut {...props} /></Suspense>;
}
export function NumberTrendWidget(props: ComponentProps<typeof CC.NumberTrendWidget>) {
  return <Suspense fallback={fallback}><LazyNumberTrend {...props} /></Suspense>;
}
